// The 9:16 badge story: its colours, its layout and its words. The share page
// (DOM) and the saved PNG (canvas) both read from here, so the picture a member
// saves is the one they see. Coordinates are pixels of the 1080 x 1920 image;
// the page scales them with --u (one image pixel in CSS pixels).
//
// Instagram lays its own header over the top ~250 px of a story and its reply
// bar over the bottom ~250 px, so everything that matters sits between.
import type { BadgeDef, Tier } from "../types";
import type { BadgeLib } from "../art";
import { FIRST_STAMP_TEXT, fmtDate, tierLine, tierLineText } from "../collection/badgeFormat";

export const STORY_W = 1080;
export const STORY_H = 1920;

export const STORY_LAYOUT = {
  pad: 72,
  /** Baseline of the top row (wordmark left, category right). */
  topY: 318,
  badge: { cx: 540, cy: 760, size: 680 },
  /** Radius of the light and the rays behind the badge. */
  lightR: 560,
  rays: 18,
  /** The "First on n450s" stamp (first-ever copies): top-left corner, before its rotation. */
  stamp: { x: 88, y: 1024, h: 116, rotate: -6, border: 7, band: 12 },
  /** The tier pill at the start of the line under the name. */
  pill: { padX: 20, border: 5, shadow: 6, gap: 18 },
  card: { x: 72, y: 1160, w: 936, pad: 52, border: 8, shadow: 16 },
  patternTile: 240,
} as const;

export interface StoryTheme {
  /** Backdrop gradient, top to bottom. */
  top: string;
  bottom: string;
  /** Soft light behind the badge. */
  light: string;
  lightAlpha: number;
  /** The rays of the light burst. */
  rays: string;
  raysAlpha: number;
  /** The faint category glyph pattern. */
  pattern: string;
  patternAlpha: number;
  /** Text colour on the backdrop (the wordmark row). */
  ink: string;
}

export const STORY_THEMES: Record<Tier, StoryTheme> = {
  PAPER: { top: "#fbf8ea", bottom: "#e6ddb8", light: "#ffffff", lightAlpha: 0.95, rays: "#000000", raysAlpha: 0.05, pattern: "#000000", patternAlpha: 0.07, ink: "#111111" },
  ROCK: { top: "#8a8a8a", bottom: "#3c3c3c", light: "#e0e0e0", lightAlpha: 0.55, rays: "#ffffff", raysAlpha: 0.09, pattern: "#000000", patternAlpha: 0.13, ink: "#ffffff" },
  BRONZE: { top: "#d8802b", bottom: "#733600", light: "#ffd2a9", lightAlpha: 0.62, rays: "#ffd2a9", raysAlpha: 0.15, pattern: "#3d1c00", patternAlpha: 0.16, ink: "#ffffff" },
  SILVER: { top: "#f1f1f1", bottom: "#969696", light: "#ffffff", lightAlpha: 0.95, rays: "#ffffff", raysAlpha: 0.32, pattern: "#000000", patternAlpha: 0.08, ink: "#111111" },
  GOLD: { top: "#ffda78", bottom: "#df9110", light: "#fff6d6", lightAlpha: 0.95, rays: "#ffffff", raysAlpha: 0.3, pattern: "#6b4300", patternAlpha: 0.12, ink: "#111111" },
  RUBY: { top: "#520910", bottom: "#140204", light: "#ff4d5e", lightAlpha: 0.58, rays: "#ff4d5e", raysAlpha: 0.15, pattern: "#ff4d5e", patternAlpha: 0.1, ink: "#ffffff" },
  EMERALD: { top: "#08432a", bottom: "#03140b", light: "#2bd98a", lightAlpha: 0.52, rays: "#2bd98a", raysAlpha: 0.14, pattern: "#2bd98a", patternAlpha: 0.1, ink: "#ffffff" },
  DIAMOND: { top: "#0f3f47", bottom: "#051417", light: "#7ff5ec", lightAlpha: 0.52, rays: "#7ff5ec", raysAlpha: 0.15, pattern: "#7ff5ec", patternAlpha: 0.1, ink: "#ffffff" },
};

export function storyTheme(tier: Tier | string): StoryTheme {
  return STORY_THEMES[tier as Tier] ?? STORY_THEMES.PAPER;
}

export const STORY_CARD = { fill: "#fdfbf2", ink: "#111111", muted: "#555555" } as const;

export function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
}

// ---------- words ----------

export interface StoryInput {
  badge: Pick<BadgeDef, "code" | "name" | "category" | "tier" | "number"> & { unique?: boolean };
  username: string;
  /** Copies the member holds (the art draws "x3" above 1). */
  count: number;
  /** When it was first earned. */
  date: string;
  holders: number;
  members: number;
  /** The member was the first on the site to earn it: the story gets the stamp. */
  firstEver: boolean;
}

export interface StoryText {
  wordmark: string;
  kicker: string;
  name: string;
  /** Name font size in image pixels: long names step down so they fit two lines. */
  nameSize: number;
  /** The whole line as text: "Ruby. 2 of 41 members have it." */
  line: string;
  /** The tier, drawn as a pill in its colour at the start of the line. */
  tier: string;
  tierLabel: string;
  /** What follows the pill: "2 of 41 members have it." */
  fact: string;
  /** "First on n450s" on a first-ever copy, else null. */
  stamp: string | null;
  by: string;
  username: string;
  date: string;
}

export function storyNameSize(name: string): number {
  const n = name.trim().length;
  if (n <= 14) return 84;
  if (n <= 22) return 72;
  return 60;
}

function humanCategory(category: string): string {
  return category.replace(/_/g, " ").toLowerCase();
}

/** The words on the story, the same for the page and the PNG. */
export function storyText(input: StoryInput, lib?: BadgeLib | null): StoryText {
  const categoryName = lib?.categories[input.badge.category]?.name || humanCategory(input.badge.category);
  const name = input.badge.name || categoryName;
  // the stamp says first on the site, so the line keeps to the tier and the holders
  const line = tierLine({
    tier: input.badge.tier,
    holders: input.holders,
    members: input.members,
    unique: input.badge.unique,
  });
  return {
    wordmark: "n450s.com",
    kicker: categoryName.toUpperCase(),
    name,
    nameSize: storyNameSize(name),
    line: tierLineText(line),
    tier: line.tier,
    tierLabel: line.label,
    fact: line.fact,
    stamp: input.firstEver ? FIRST_STAMP_TEXT : null,
    by: input.count > 1 ? `earned ${input.count} times by` : "earned by",
    username: input.username,
    date: fmtDate(input.date),
  };
}

// ---------- the glyph pattern ----------

/**
 * A 240 x 240 pattern tile: the category's mini glyph twice, staggered, flattened
 * to one colour. Falls back to the tier's base shape, then to dots.
 */
export function patternTileSvg(
  lib: BadgeLib | null | undefined,
  category: string,
  tier: string,
  color: string,
  alpha: number
): string {
  const T = STORY_LAYOUT.patternTile;
  const glyphId = lib?.categories[category]?.miniGlyph || lib?.tokens.tiers[tier]?.base;
  const part = glyphId ? lib?.parts[glyphId] : undefined;
  let cells: string;
  if (part && part.viewBox?.length === 4) {
    const [vx, vy, w, h] = part.viewBox;
    const s = 76 / Math.max(w, h);
    const r = (n: number) => Math.round(n * 1000) / 1000;
    // one colour: every fill and stroke becomes the pattern colour, "none" stays none
    const flat = part.body.replace(/\s(fill|stroke)="(?!none")[^"]*"/g, (_m, attr: string) => ` ${attr}="${color}"`);
    const place = (x: number, y: number) =>
      `<g transform="translate(${x} ${y}) rotate(-14) scale(${r(s)}) translate(${r(-(vx + w / 2))} ${r(-(vy + h / 2))})">${flat}</g>`;
    cells = place(T * 0.25, T * 0.25) + place(T * 0.75, T * 0.75);
  } else {
    cells = `<circle cx="${T * 0.25}" cy="${T * 0.25}" r="9" fill="${color}"/><circle cx="${T * 0.75}" cy="${T * 0.75}" r="9" fill="${color}"/>`;
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${T}" height="${T}" viewBox="0 0 ${T} ${T}">` +
    `<g opacity="${alpha}">${cells}</g></svg>`
  );
}
